import Checkbox from "@material-ui/core/Checkbox";
import Collapse from "@material-ui/core/Collapse";
import EditIcon from "@material-ui/icons/Edit";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import DomainTagList from "./DomainTagList";
import { BrandgenPublicationList } from "../../../../types/campaignTypes";

type Variant = "public" | "custom";

export type PublicationListsTableProps = {
  lists: BrandgenPublicationList[];
  variant: Variant;
  // Selection
  isSelectionMode: boolean;
  isSplitMode: boolean;
  panelMode?: boolean;
  selectedListIds: string[];
  selectedDomainIds: string[];
  onListSelect?: (listId: string) => void;
  onDomainSelect?: (domainId: string) => void;
  // Actions
  onEditClick?: (list: BrandgenPublicationList) => void;
};

// ─── Styled components ────────────────────────────────────────────────────────

const StyledTableHead = styled(TableHead)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
  "& th": {
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.brand.gray500,
    padding: "10px 16px",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  },
}));

// Row: clickable (selection or management-expand on click)
const ClickableRow = styled(TableRow)(({ theme }: { theme: Theme }) => ({
  height: 48,
  cursor: "pointer",
  "&:hover": {
    background: theme.palette.brand.gray50,
  },
  "& td": {
    padding: "0 16px",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
    fontSize: 13,
    color: theme.palette.brand.gray900,
  },
}));

// Expand-content row
const ExpandRow = styled(TableRow)(({ theme }: { theme: Theme }) => ({
  "& td": {
    padding: "0 !important",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  },
}));

const CountBadge = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  marginLeft: 6,
  padding: "1px 7px",
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 500,
  background: theme.palette.brand.gray100,
  color: theme.palette.brand.gray500,
  border: `1px solid ${theme.palette.brand.gray200}`,
}));

const EmptyCell = styled(TableCell)(({ theme }: { theme: Theme }) => ({
  textAlign: "center",
  padding: "24px 16px !important",
  color: `${theme.palette.brand.gray400} !important`,
  fontSize: "13px !important",
}));

const CoralCheckbox = styled(Checkbox)(({ theme }: { theme: Theme }) => ({
  "&.Mui-checked": {
    color: theme.palette.brand.coral,
  },
}));

const EditButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 6,
  padding: 4,
  background: theme.palette.brand.white,
  "&:hover": {
    background: theme.palette.brand.gray100,
  },
}));

const ListName = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.brand.gray900,
}));

const ExpandIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  padding: 2,
  color: theme.palette.brand.gray500,
}));

// ─── Component ────────────────────────────────────────────────────────────────

const PublicationListsTable: FC<PublicationListsTableProps> = ({
  lists,
  variant,
  isSelectionMode,
  isSplitMode,
  panelMode = false,
  selectedListIds,
  selectedDomainIds,
  onListSelect,
  onDomainSelect,
  onEditClick,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandRow = (listId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) {
        next.delete(listId);
      } else {
        next.add(listId);
      }
      return next;
    });
  };

  const handleRowClick = (listId: string) => {
    if (isSelectionMode && !panelMode) {
      // Stepper mode: row click = select/deselect; chevron handles expand
      onListSelect?.(listId);
    } else {
      // Management + panel mode: row click = expand/collapse
      expandRow(listId);
    }
  };

  const showEditColumn = variant === "custom" && !panelMode;

  // colSpan for the expand row and empty state
  const colSpan = 2 + (isSelectionMode ? 1 : 0) + (showEditColumn ? 1 : 0);

  return (
    <Table>
      <StyledTableHead>
        <TableRow>
          {isSelectionMode && <TableCell style={{ width: 32, padding: "10px 8px" }} />}
          <TableCell>List Name</TableCell>
          <TableCell>Active Domains</TableCell>
          {showEditColumn && <TableCell style={{ width: 52 }} />}
        </TableRow>
      </StyledTableHead>

      <TableBody>
        {lists.length === 0 && (
          <TableRow>
            <EmptyCell colSpan={colSpan}>No lists found.</EmptyCell>
          </TableRow>
        )}

        {lists.map((list) => {
          const isExpanded = expandedIds.has(list.id);
          const isSelected = selectedListIds.includes(list.id);
          const activeDomainCount = list.listDomains?.filter((d) => d.active).length ?? 0;

          return (
            <React.Fragment key={list.id}>
              <ClickableRow onClick={() => handleRowClick(list.id)}>
                {isSelectionMode && (
                  <TableCell
                    style={{ width: 32, paddingLeft: 8, paddingRight: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <CoralCheckbox
                      checked={isSelected}
                      onChange={() => onListSelect?.(list.id)}
                      size="small"
                      style={{ padding: 2 }}
                    />
                  </TableCell>
                )}

                <TableCell>
                  <ListName>{list.name}</ListName>
                </TableCell>

                <TableCell>
                  <span style={{ display: "flex", alignItems: "center" }}>
                    <ExpandIconButton
                      size="small"
                      aria-label={isExpanded ? "collapse" : "expand"}
                      onClick={(e) => toggleExpand(list.id, e)}
                    >
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </ExpandIconButton>
                    <CountBadge>{activeDomainCount}</CountBadge>
                  </span>
                </TableCell>

                {showEditColumn && (
                  <TableCell align="right" style={{ paddingRight: 12 }} onClick={(e) => e.stopPropagation()}>
                    <EditButton size="small" aria-label="edit list" onClick={() => onEditClick?.(list)}>
                      <EditIcon fontSize="small" />
                    </EditButton>
                  </TableCell>
                )}
              </ClickableRow>

              <ExpandRow>
                <TableCell colSpan={colSpan}>
                  <Collapse in={isExpanded} unmountOnExit>
                    {list.listDomains ? (
                      <DomainTagList
                        domains={list.listDomains}
                        isSelectable={isSelectionMode && isSplitMode}
                        selectedDomainIds={selectedDomainIds}
                        onDomainSelect={onDomainSelect}
                      />
                    ) : null}
                  </Collapse>
                </TableCell>
              </ExpandRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default PublicationListsTable;
